import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useBulkSelection } from "@/hooks/useBulkSelection";
import useTableRowMenu from "@/hooks/useTableRowMenu";
import { useParams } from "@/hooks/useSearchParams";
import { TableHeading } from "@/components/Table";
import PersonSideSheet from "@/features/person/components/PersonSideSheet";
import { useNavigate } from "@/router";
import { MEMBER_ELECTIONS_PAST_PATH, MEMBER_ELECTIONS_PAST_PRESIDENTS_PATH } from "../constants";
import { buildMemberElectionPersonPath } from "../_utils/buildMemberElectionPersonPath";
import { usePastMemberElectionRuns } from "../_hooks/usePastMemberElectionRuns";
import type { MemberElectionRunNode } from "../types";
import PastMemberElectionRunsTable from "./PastMemberElectionRunsTable";

function syncSelectedRunCache(
  cache: Record<string, MemberElectionRunNode>,
  runs: MemberElectionRunNode[],
  rowSelection: Record<string, boolean>
) {
  let changed = false;

  for (const run of runs) {
    if (rowSelection[run.id] && cache[run.id] !== run) {
      cache[run.id] = run;
      changed = true;
    }
  }

  for (const id of Object.keys(cache)) {
    if (!rowSelection[id]) {
      delete cache[id];
      changed = true;
    }
  }

  return changed;
}

export default function PastMemberElectionsContent() {
  const navigate = useNavigate();
  const { searchParamsUrl } = useParams();
  const bulkSelection = useBulkSelection();
  const { Menu, setMenu } = useTableRowMenu();
  const [isPersonSideSheetOpen, setIsPersonSideSheetOpen] = useState(false);
  const [selectedPgaId, setSelectedPgaId] = useState<string | undefined>(undefined);
  const { rowSelection, rowSelectionCount, isSelectedOnly, deselectAll } = bulkSelection;
  const {
    runs,
    loading,
    showLoadError,
    createHeaderPagination,
    createFooterPagination,
    searchValue,
    setSearchValue,
    clearSearch,
    searchActive
  } = usePastMemberElectionRuns({ deselectAll });
  const cacheRef = useRef<Record<string, MemberElectionRunNode>>({});
  const selectedRunByIdRef = useRef<Record<string, MemberElectionRunNode>>({});

  const selectedRunById = useMemo(() => {
    const changed = syncSelectedRunCache(cacheRef.current, runs, rowSelection);
    if (!changed) {
      return selectedRunByIdRef.current;
    }

    const next = { ...cacheRef.current };
    selectedRunByIdRef.current = next;
    return next;
  }, [runs, rowSelection]);

  useEffect(() => {
    if (rowSelectionCount === 0 && isSelectedOnly) {
      deselectAll();
    }
  }, [rowSelectionCount, isSelectedOnly, deselectAll]);

  const openPersonSideSheet = useCallback((pgaId: string) => {
    setSelectedPgaId(pgaId);
    setIsPersonSideSheetOpen(true);
  }, []);

  const closePersonSideSheet = useCallback(() => {
    setIsPersonSideSheetOpen(false);
    setSelectedPgaId(undefined);
  }, []);

  const goToPastPresidents = useCallback(() => {
    navigate(MEMBER_ELECTIONS_PAST_PRESIDENTS_PATH);
  }, [navigate]);

  return (
    <>
      <Menu />
      <TableHeading
        heading="Past Elections"
        body="View completed runs of finalized member elections, including who started each run, how many applications were processed, and the outcome."
        actions={[
          {
            label: "Edit Past Presidents",
            variant: "outlined",
            onClick: goToPastPresidents,
            testId: "edit-past-presidents"
          }
        ]}
      />
      <div className="w-full">
        <PastMemberElectionRunsTable
          loading={loading}
          runs={runs}
          selectedRunById={selectedRunById}
          showLoadError={showLoadError}
          bulkSelection={bulkSelection}
          createHeaderPagination={createHeaderPagination}
          createFooterPagination={createFooterPagination}
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          clearSearch={clearSearch}
          hasActiveSearch={searchActive}
          setMenu={setMenu}
          onViewPerson={openPersonSideSheet}
        />
      </div>
      <PersonSideSheet
        open={isPersonSideSheetOpen}
        onClose={closePersonSideSheet}
        pgaId={selectedPgaId ?? null}
        showEmployment={true}
        showFacility={true}
        customActionButtons={
          selectedPgaId
            ? [
                {
                  text: "View Person",
                  variant: "outlined" as const,
                  onClick: () => {
                    const personPath = buildMemberElectionPersonPath(
                      selectedPgaId,
                      searchParamsUrl,
                      false,
                      MEMBER_ELECTIONS_PAST_PATH
                    );
                    if (personPath) {
                      navigate(personPath);
                    }
                  }
                }
              ]
            : undefined
        }
      />
    </>
  );
}
